import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isNavigationAbortedFetch } from '@/lib/pageLifecycle';

export interface BreakGlassSessionState {
  active: boolean;
  expiresAt: string | null;
  sessionId: string | null;
  organizationId: string | null;
  role: string | null;
  status: string | null;
}

const EMPTY_STATE: BreakGlassSessionState = {
  active: false,
  expiresAt: null,
  sessionId: null,
  organizationId: null,
  role: null,
  status: null,
};

/**
 * Hook to check and track break glass session state for the current user.
 * Calls the check_breakglass_session RPC on mount and provides live state.
 */
export function useBreakGlassSession(options: { enabled?: boolean; organizationId?: string | null } = {}) {
  const { enabled = true, organizationId } = options;
  const [state, setState] = useState<BreakGlassSessionState>(EMPTY_STATE);
  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkSession = useCallback(async () => {
    if (!enabled || !organizationId) {
      setState(EMPTY_STATE);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('check_breakglass_session', {
        p_organization_id: organizationId,
      });
      if (error) {
        // ENT-6086 (the ENT-5715 class): a check the browser cancelled because the page is
        // navigating away is not a failure; the session state still resets to empty.
        if (!isNavigationAbortedFetch(error)) console.error('Failed to check break glass session:', error);
        setState(EMPTY_STATE);
        return;
      }

      if (data && typeof data === 'object') {
        setState({
          active: data.active ?? false,
          expiresAt: data.expires_at ?? null,
          sessionId: data.session_id ?? null,
          organizationId: data.organization_id ?? null,
          role: data.role ?? null,
          status: data.status ?? null,
        });
      } else {
        setState(EMPTY_STATE);
      }
    } catch (err) {
      if (!isNavigationAbortedFetch(err)) console.error('Break glass session check error:', err);
      setState(EMPTY_STATE);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, organizationId]);

  useEffect(() => {
    checkSession();

    // Re-check on navigation (visibility change) rather than polling
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkSession();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkSession]);

  return {
    ...state,
    isLoading,
    refresh: checkSession,
  };
}
